import { showSettings, wireControls } from "./controls-panel.js";
import { asConvertOptions, optionFields, type ToolOptions } from "./options.js";
import type { ConvertOptions } from "@kensio/pinyinjs";
import { browserStorage, loadOptions, saveOptions } from "./settings-store.js";

/**
 * The settings, from the last visit to the next.
 *
 * Loading them, drawing the panel, saving a change and telling the tool to
 * re-convert are one job rather than four: every one of them happens because
 * of one of the others, and none of them is interesting to the conversion
 * itself, which only ever wants the current `ConvertOptions`.
 */
export class ToolSettings {
  readonly #storage = browserStorage();

  #options: ToolOptions;

  /**
   * Show the settings in the panel the page already has, and report every
   * change.
   *
   * The panel arrives with the defaults checked, so the first thing this does
   * is move the radios the last visit or the link disagrees with. The ids and
   * names that keep two panels on one page apart are the template's now,
   * which is why there is no longer a prefix to pass.
   *
   * `fromUrl` wins over what the last visit stored, and both over the
   * defaults. A link somebody was sent says what that link is for, and it
   * would be a strange thing to open and find showing something else — but it
   * only speaks for the settings it names, so the rest still come from this
   * device.
   */
  constructor(
    panel: Element,
    fromUrl: Partial<ToolOptions>,
    onChange: () => void,
  ) {
    this.#options = { ...loadOptions(this.#storage), ...fromUrl };

    showSettings(panel, this.#options);

    wireControls<ToolOptions>(panel, optionFields, (changed) => {
      this.#options = { ...this.#options, ...changed };
      saveOptions(this.#storage, this.#options);
      onChange();
    });
  }

  /** The settings as the conversion wants them. */
  get convertOptions(): ConvertOptions {
    return asConvertOptions(this.#options);
  }

  /** The settings in full, which is what the address bar is written from. */
  get options(): ToolOptions {
    return this.#options;
  }
}
